import { Alert, Snackbar } from "@mui/material";
import * as React from "react";

import { CopyLinkButton } from "@/shared/components/CopyLinkButton";
import { StatusBadge, STATUS_BADGES } from "@/shared/components/StatusBadge";
import { FormattedDateTime } from "@/shared/datetime";
import { type AdminFeedbackLabelCount } from "@/shared/domain/admin/AdminService";
import {
  useAdminFeedbackLabels,
  useUpdateAdminFeedback,
} from "@/shared/domain/admin/hooks/useAdminFeedback";
import {
  type AgentFeedbackRecord,
  FEEDBACK_CATEGORY_LABELS,
} from "@/shared/domain/agentThread";
import {
  Autocomplete,
  Card,
  Field,
  RobotoButton,
  Tag,
  Textarea,
} from "@/shared/foundation/primitives";
import { ErrorMonitoringService } from "@/shared/services/ErrorMonitoringService";

import { FeedbackSentimentIcon } from "./FeedbackSentimentIcon";
import styles from "./FeedbackTriagePanel.module.css";
import {
  buildSavePayload,
  isDirty as isFormDirty,
} from "./feedbackTriageState";
import { buildAdminFeedbackLink } from "./shareLinks";

interface FeedbackTriagePanelProps {
  feedback: AgentFeedbackRecord;
  /**
   * Called with the server's updated record after each successful save or
   * resolve toggle. The parent must pass it back as ``feedback``: the panel
   * compares the form and the resolved state against that prop.
   */
  onUpdated: (feedback: AgentFeedbackRecord) => void;
}

interface Toast {
  severity: "success" | "error";
  message: string;
}

/**
 * Detail card for the selected feedback row: what the user said (sentiment,
 * categories, and the whole note, which the table can only truncate) beside
 * the admin-only triage form (``admin_label``, ``admin_note``, ``resolved``).
 *
 * The label field suggests the labels already in use, most-used first, so
 * admins converge on a shared vocabulary instead of near-duplicates. Changes
 * are sent via ``PUT /v1/admin/feedback/<id>``; on success
 * ``useUpdateAdminFeedback`` invalidates the listing and the label suggestions.
 */
export const FeedbackTriagePanel: React.FC<FeedbackTriagePanelProps> = ({
  feedback,
  onUpdated,
}) => {
  const updater = useUpdateAdminFeedback();
  const labelSuggestions = useAdminFeedbackLabels();
  const [label, setLabel] = React.useState<string>(feedback.admin_label ?? "");
  const [note, setNote] = React.useState<string>(feedback.admin_note ?? "");
  const [toast, setToast] = React.useState<Toast | null>(null);

  const showMutationError = (
    error: Error,
    action: "save" | "toggleResolved",
  ) => {
    ErrorMonitoringService.captureError(error, {
      context: { feedbackId: feedback.feedback_id, action },
    });
    setToast({
      severity: "error",
      message: `Couldn't update feedback: ${error.message}`,
    });
  };

  // Reset local form state when a *different* feedback record is shown.
  // Keyed only on ``feedback_id``: a refetch that returns the same row with
  // updated triage columns must not blow away whatever the admin has typed
  // since opening the panel.
  React.useEffect(() => {
    setLabel(feedback.admin_label ?? "");
    setNote(feedback.admin_note ?? "");
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- intentional; see comment above
  }, [feedback.feedback_id]);

  const dirty = isFormDirty({
    persistedLabel: feedback.admin_label ?? null,
    persistedNote: feedback.admin_note ?? null,
    draftLabel: label,
    draftNote: note,
  });

  const save = () => {
    updater.mutate(
      {
        feedbackId: feedback.feedback_id,
        request: buildSavePayload({ draftLabel: label, draftNote: note }),
      },
      {
        onSuccess: (updated) => {
          onUpdated(updated);
          setToast({ severity: "success", message: "Feedback updated" });
        },
        onError: (error) => showMutationError(error, "save"),
      },
    );
  };

  const toggleResolved = () => {
    const nextResolved = !feedback.resolved;
    updater.mutate(
      {
        feedbackId: feedback.feedback_id,
        request: { resolved: nextResolved },
      },
      {
        onSuccess: (updated) => {
          onUpdated(updated);
          setToast({
            severity: "success",
            message: nextResolved ? "Marked resolved" : "Re-opened",
          });
        },
        onError: (error) => showMutationError(error, "toggleResolved"),
      },
    );
  };

  return (
    <Card className={styles.panel} role="region" aria-label="Selected feedback">
      <Card.Header>
        <Card.Title>
          Feedback on message #{feedback.message_sequence_num}
        </Card.Title>
        <Card.Actions>
          <StatusBadge
            status={
              feedback.resolved
                ? STATUS_BADGES.agentFeedback.resolved
                : STATUS_BADGES.agentFeedback.open
            }
          />
          <CopyLinkButton
            url={buildAdminFeedbackLink({
              sessionId: feedback.thread_id,
              feedbackId: feedback.feedback_id,
            })}
            tooltip="Copy a link to this feedback for another admin"
          />
        </Card.Actions>
        <Card.Description>
          <span className={styles.meta}>
            {feedback.created_by} · {feedback.org_id} ·{" "}
            <FormattedDateTime value={feedback.created} />
          </span>
        </Card.Description>
      </Card.Header>
      <Card.Body className={styles.body}>
        <section className={styles.column} aria-label="What the user said">
          <h3 className={styles.sectionTitle}>What the user said</h3>
          <div className={styles.verdict}>
            <FeedbackSentimentIcon sentiment={feedback.sentiment} />
            {feedback.categories.map((category) => (
              <Tag
                key={category}
                label={FEEDBACK_CATEGORY_LABELS[category]}
                size="compact"
              />
            ))}
          </div>
          {feedback.notes ? (
            <blockquote
              className={styles.userNote}
              data-testid="feedback-user-note"
            >
              {feedback.notes}
            </blockquote>
          ) : (
            <p className={styles.empty}>The user left no note.</p>
          )}
        </section>

        <section className={styles.column} aria-label="Triage">
          <h3 className={styles.sectionTitle}>Triage</h3>
          <Autocomplete
            items={labelSuggestions.data ?? []}
            value={label}
            onValueChange={setLabel}
            itemToStringValue={(item) => item.label}
            label="Admin label"
            size="sm"
            openOnFocus
          >
            <Autocomplete.Input placeholder="e.g. follow-up, dup, data-quality" />
            <Autocomplete.Content>
              <Autocomplete.List>
                {(item: AdminFeedbackLabelCount) => (
                  <Autocomplete.Item key={item.label} value={item}>
                    <span className={styles.labelOption}>
                      <Autocomplete.MatchText text={item.label} />
                      <span className={styles.labelCount}>{item.count}</span>
                    </span>
                  </Autocomplete.Item>
                )}
              </Autocomplete.List>
            </Autocomplete.Content>
          </Autocomplete>
          <Field.Root>
            <Field.Label>Admin note</Field.Label>
            <Field.Control
              render={<Textarea rows={3} />}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Internal-only notes"
            />
          </Field.Root>
          <div className={styles.actions}>
            {feedback.resolved &&
              feedback.resolved_by &&
              feedback.resolved_at && (
                <span className={styles.resolvedMeta}>
                  Resolved by {feedback.resolved_by} on{" "}
                  <FormattedDateTime value={feedback.resolved_at} />
                </span>
              )}
            <RobotoButton
              variant="secondary"
              size="sm"
              onClick={toggleResolved}
              disabled={updater.isPending}
            >
              {feedback.resolved ? "Re-open" : "Mark resolved"}
            </RobotoButton>
            <RobotoButton
              variant="primary"
              size="sm"
              onClick={save}
              disabled={!dirty || updater.isPending}
            >
              Save
            </RobotoButton>
          </div>
        </section>
      </Card.Body>
      {/* Mount the Snackbar only when there is a toast to show, so its
          child is always a real Alert and not ``undefined`` (MUI tolerates
          ``undefined`` but logs a warning, and the conditional makes the
          intent obvious). */}
      {toast !== null && (
        <Snackbar
          open
          autoHideDuration={toast.severity === "error" ? 6000 : 2500}
          onClose={() => setToast(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            severity={toast.severity}
            variant="filled"
            onClose={() => setToast(null)}
          >
            {toast.message}
          </Alert>
        </Snackbar>
      )}
    </Card>
  );
};
